import Header from '../components/Header';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';
import { Star, ArrowRight, Award } from 'lucide-react';

const Reviews = () => {
  const reviews = [
    {
      id: 1,
      name: 'Placard',
      slug: 'placard',
      bonus: 'Welcome Bonus: 100% + 30 Free Spins',
      rating: 4.5,
      description: 'Comprehensive review of Placard casino, covering game selection, bonuses, payment methods, and overall player experience.'
    },
    {
      id: 2,
      name: '888',
      slug: '888',
      bonus: 'Welcome Bonus: Up to £1000',
      rating: 4.8,
      description: 'In-depth analysis of 888 casino, examining licensing, customer support, withdrawal speeds, and gaming variety.'
    },
    {
      id: 3,
      name: 'Betano',
      slug: 'betano',
      bonus: '100% up to £60 + £10 Free Bet',
      rating: 4.6,
      description: 'Detailed review of Betano casino, evaluating user experience, security measures, bonus terms, and game quality.'
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      <div className="pt-32 pb-20 px-4">
        <div className="container mx-auto max-w-7xl">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-pink-500 to-green-500 rounded-full mb-6">
              <Star className="w-10 h-10 text-white" />
            </div>
            <h1 className="pb-2 text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-pink-600 to-green-600 bg-clip-text text-transparent">
              Expert Casino Reviews
            </h1>
            <p className="text-xl md:text-2xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              Comprehensive, independent reviews of the UK's leading online casinos. Our detailed analyses help you make informed decisions about where to play.
            </p>
          </div>

          {/* Reviews Grid */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {reviews.map((review) => (
              <Link
                key={review.id}
                to={`/reviews/${review.slug}`}
                className="glass-strong rounded-3xl overflow-hidden group hover:transform hover:scale-105 transition-all duration-300 flex flex-col"
                data-discover="true"
              >
                {/* Casino Image */}
                <div className="relative h-64 bg-gradient-to-br from-pink-200 via-green-200 to-pink-300 overflow-hidden" style={{ backgroundImage: `url("/images/${review.id === 1 ? 'aidan-howe-xrR-Kr2zppo-unsplash.jpg' : review.id === 2 ? 'aldric-rivat-CltI5xgDAs0-unsplash.jpg' : 'amol-tyagi-7dkLbNE6ycI-unsplash.jpg'}")`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                  <div className="absolute top-4 right-4">
                    <div className="flex items-center space-x-1 glass px-3 py-1 rounded-full">
                      <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                      <span className="text-white font-bold text-sm">{review.rating}</span>
                    </div>
                  </div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="glass px-4 py-2 rounded-lg">
                      <span className="text-white font-bold text-2xl">{review.name}</span>
                    </div>
                  </div>
                </div>

                <div className="p-6 flex-grow flex flex-col">
                  <div className="flex items-center space-x-2 mb-3">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`w-5 h-5 ${
                          star <= Math.round(review.rating)
                            ? 'text-yellow-400 fill-yellow-400'
                            : 'text-gray-300'
                        }`}
                      />
                    ))}
                    <span className="text-gray-600 font-semibold ml-2">{review.rating}/5.0</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-2 group-hover:text-green-600 transition-colors">
                    {review.name} Casino Review
                  </h3>
                  <p className="text-green-600 font-semibold mb-3">{review.bonus}</p>
                  <p className="text-gray-600 mb-4 leading-relaxed flex-grow">
                    {review.description}
                  </p>
                  <div className="flex items-center text-green-600 font-semibold group-hover:translate-x-2 transition-transform">
                    <span>Read Full Review</span>
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* About Our Reviews Section */}
          <div className="glass-strong rounded-3xl p-8 md:p-12">
            <div className="flex items-center space-x-3 mb-6">
              <Award className="w-8 h-8 text-green-600" />
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800">How We Review Casinos</h2>
            </div>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-6">
              <p>
                Our casino reviews represent comprehensive, independent evaluations conducted through extensive testing, analysis, and verification processes. We approach each review with complete editorial independence, ensuring that our assessments reflect genuine quality rather than commercial considerations or affiliate relationships.
              </p>

              <p>
                Our review process begins with account creation and actual gameplay testing. We register accounts, make deposits using various payment methods, explore game libraries extensively, test customer support responsiveness, examine withdrawal procedures, and evaluate overall user experience. This hands-on approach provides insights that theoretical analysis cannot reveal, ensuring our reviews reflect real-world experiences rather than marketing claims.
              </p>

              <p>
                We evaluate casinos across multiple critical dimensions: licensing and regulatory compliance verification through UK Gambling Commission databases, game variety assessment examining both quantity and quality of offerings, software provider partnerships ensuring access to premium content, bonus terms analysis evaluating wagering requirements and conditions, payment method availability and processing times, withdrawal speed and reliability testing, customer support quality through multiple contact attempts, user interface design and mobile optimization, responsible gambling tool accessibility and effectiveness, and security measures including SSL encryption and data protection standards.
              </p>

              <p>
                Our rating system reflects comprehensive evaluation across all these dimensions, with final ratings representing weighted assessments of overall casino quality. We don't inflate ratings for commercial reasons or reduce them unfairly – our ratings reflect genuine quality assessments based on extensive testing and industry expertise.
              </p>

              <p>
                Each review includes detailed analysis of strengths and weaknesses, providing balanced perspectives that help readers understand not just overall quality, but specific aspects where casinos excel or fall short. We highlight exceptional features that distinguish casinos from competitors, while also addressing areas where improvements would enhance player experiences.
              </p>

              <p>
                Our reviews maintain complete transparency regarding our testing methodologies, rating criteria, and any limitations in our evaluations. We clearly distinguish between confirmed facts from our testing and information obtained from operator communications or industry sources. This transparency ensures readers understand the basis for our assessments and can make informed decisions accordingly.
              </p>

              <p>
                While we may earn commissions from casinos we review and recommend, these commercial relationships never influence our editorial assessments, rating methodology, or review content. Our affiliate relationships enable us to maintain comprehensive review coverage while ensuring editorial independence remains absolute. Every review reflects genuine testing and analysis, regardless of commercial considerations.
              </p>

              <p>
                We continuously update reviews to reflect changes in casino operations, new game releases, updated terms and conditions, regulatory developments, and evolving industry standards. Our commitment to current, accurate reviews ensures that our assessments remain valuable resources for players navigating the dynamic UK online casino market.
              </p>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Reviews;

